import {
  Bold,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  Italic,
  Link,
  Link2Off,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Redo,
  Strikethrough,
  Undo,
  WrapText,
} from "lucide-react";
import { useCallback } from "react";

import { Editor } from "@tiptap/react";

import { Button } from "../ui/button";

type WyswygToolbarProps = {
  editor: Editor;
};

function WyswygToolbar(props: WyswygToolbarProps) {
  const { editor } = props;

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();

      return;
    }

    // update link
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  return (
    <div className="grid grid-cols-8 gap-2">
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={
          !editor.can().chain().focus().toggleBold().run() || !editor.isEditable
        }
        className={editor.isActive("bold") ? "is-active" : ""}
      >
        <Bold className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={
          !editor.can().chain().focus().toggleItalic().run() ||
          !editor.isEditable
        }
        className={editor.isActive("italic") ? "is-active" : ""}
      >
        <Italic className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={
          !editor.can().chain().focus().toggleStrike().run() ||
          !editor.isEditable
        }
        className={editor.isActive("strike") ? "is-active" : ""}
      >
        <Strikethrough className="size-4" />
      </Button>

      {/* <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().unsetAllMarks().run()}
      >
        clear marks
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().clearNodes().run()}
      >
        clear nodes
      </Button> */}
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().setParagraph().run()}
        className={editor.isActive("paragraph") ? "is-active" : ""}
        disabled={
          !editor.can().chain().focus().setParagraph().run() ||
          !editor.isEditable
        }
      >
        <Pilcrow className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editor.isActive("heading", { level: 1 }) ? "is-active" : ""}
        disabled={
          !editor.can().chain().focus().toggleHeading({ level: 1 }).run() ||
          !editor.isEditable
        }
      >
        <Heading1 className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={editor.isActive("heading", { level: 2 }) ? "is-active" : ""}
        disabled={
          !editor.can().chain().focus().toggleHeading({ level: 2 }).run() ||
          !editor.isEditable
        }
      >
        <Heading2 className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={editor.isActive("heading", { level: 3 }) ? "is-active" : ""}
        disabled={
          !editor.can().chain().focus().toggleHeading({ level: 3 }).run() ||
          !editor.isEditable
        }
      >
        <Heading3 className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
        className={editor.isActive("heading", { level: 4 }) ? "is-active" : ""}
        disabled={
          !editor.can().chain().focus().toggleHeading({ level: 4 }).run() ||
          !editor.isEditable
        }
      >
        <Heading4 className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
        className={editor.isActive("heading", { level: 5 }) ? "is-active" : ""}
        disabled={
          !editor.can().chain().focus().toggleHeading({ level: 5 }).run() ||
          !editor.isEditable
        }
      >
        <Heading5 className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
        className={editor.isActive("heading", { level: 6 }) ? "is-active" : ""}
        disabled={
          !editor.can().chain().focus().toggleHeading({ level: 6 }).run() ||
          !editor.isEditable
        }
      >
        <Heading6 className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive("bulletList") ? "is-active" : ""}
        disabled={
          !editor.can().chain().focus().toggleBulletList().run() ||
          !editor.isEditable
        }
      >
        <List className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive("orderedList") ? "is-active" : ""}
        disabled={
          !editor.can().chain().focus().toggleOrderedList().run() ||
          !editor.isEditable
        }
      >
        <ListOrdered className="size-4" />
      </Button>

      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        disabled={
          !editor.can().chain().focus().setHorizontalRule().run() ||
          !editor.isEditable
        }
      >
        <Minus className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().setHardBreak().run()}
        disabled={
          !editor.can().chain().focus().setHardBreak().run() ||
          !editor.isEditable
        }
      >
        <WrapText className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={
          !editor.can().chain().focus().undo().run() || !editor.isEditable
        }
      >
        <Undo className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={
          !editor.can().chain().focus().redo().run() || !editor.isEditable
        }
      >
        <Redo className="size-4" />
      </Button>
      <Button
        variant={editor.isActive("link") ? "default" : "outline"}
        size="icon"
        type="button"
        onClick={setLink}
        className={editor.isActive("link") ? "is-active" : ""}
      >
        <Link className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive("link")}
      >
        <Link2Off className="size-4" />
      </Button>
    </div>
  );
}

export { WyswygToolbar };
